// オーナーへの請求 (プラットフォーム → 店舗オーナー) の API。
// 配置ルールは index.ts のヘッダ参照。
//
// storeOwner.ts (店舗の運営操作) とも admin.ts (運営専用) とも別ドメイン:
// ここは「オーナーが自分の請求を見て支払う」口。Stripe の連結アカウントが
// オーナー単位なので、複数店舗を持つオーナーでも明細は 1 本にまとまる。
import { apiFetch } from './_client'

/** #1689 移行の告知の段階。強める順に consent → warning → overdue。 */
export type SaasNoticeStage = 'none' | 'consent' | 'warning' | 'overdue' | 'unpaid'

export type SaasOffer = {
  planId: string
  monthlyFeeJpy: number
  /** 課金開始日 = 申込みの期限。null なら未定 */
  billingStartAt: string | null
  announceAt: string | null
  warningDays: number
  /** 提示条件のハッシュ。**申込み時にそのまま送り返す**（サーバが照合する） */
  offerHash: string
}

export type SaasSubscriptionState = {
  offer: SaasOffer
  /** 申込み済みなら契約レコード。未申込みは null */
  subscription: {
    planId: string
    monthlyFeeJpy: number
    billingStartAt: string | null
    agreedAt: string
  } | null
  notice: {
    stage: SaasNoticeStage
    daysLeft: number | null
    deadlineAt: string | null
    /** 未申込みのまま放置された場合に休会になる日時（催告期間の終わり） */
    suspendAt: string | null
    /** overdue のときだけ入る。あと何日で止まるか */
    daysUntilSuspend: number | null
    /** unpaid のときだけ入る。決済が通らなかった月と理由 */
    failedMonth: string | null
    failureCode: string | null
  }
  /** カード未登録では申し込めない（回収手段が無いまま契約させない） */
  hasPaymentMethod: boolean
  nextBillingAt: string | null
  /** まだ取れていない月。空でなければ「今すぐ払う」を出す */
  outstandingMonths: string[]
  /** 解約済みのとき、いつまで使えるか。未解約なら null */
  cancelled: { cancelledAt: string | null; accessEndsAt: string | null } | null
}

export interface BillingPerStore {
  storeId: string
  storeName: string
  ops: number
  amountJpy: number
}

export interface BillingItem {
  type: string
  amountJpy: number
  perStore: BillingPerStore[]
}

export interface BillingSummary {
  ownerId: string
  month: string
  items: BillingItem[]
  totalJpy: number
  paidJpy: number
  unpaidJpy: number
  capJpy: number
  /** #1552 弊社が無償で持つ分（店舗データの閲覧）。ここまでは請求されない。 */
  freeAllowanceJpy?: number
  /** #1552 同じく AI 利用の無償分。 */
  aiFreeAllowanceJpy?: number
  /** #1552 AI 側の上限（無償分 + 買い足した分）。 */
  aiCapJpy?: number
  blocked: boolean
  resetDate: string
  /** 金額は税込 (内税)。アプリ全体の方針に揃えてある。 */
  taxIncluded: boolean
  billingEnabled: boolean
  /** 1 回の支払いで買い足す枠 (税込)。 */
  bucketJpy: number
}

export interface BillingCharge {
  id: string
  type: string
  month: string
  amountJpy: number
  status: 'pending' | 'paid' | 'failed'
  stripeChargeId: string | null
  failureReason: string | null
  createdAt: string | null
  perStore: BillingPerStore[]
}

/** #1655 チャージバック保証の加入状態と当月の収支 */
export interface ChargebackGuarantee {
  month: string
  /** #1729 有料プラン未契約で加入できない (加入前だけ true)。画面は申込みへ誘導する */
  planRequired?: boolean
  enrollment: {
    /** 契約期間中か (= 補填の対象か)。解約しても満了までは true */
    enrolled: boolean
    /** 次期を更新するか。解約を押すと false になるが期間中の保証は続く */
    autoRenew: boolean
    termStartAt?: string | null
    termEndAt?: string | null
    cancelledAt?: string | null
    /** 加入時に確定した年間保険料と上限。当月の売上から引き直さない */
    annualPremiumJpy: number
    annualCapJpy: number
  }
  quote: {
    cardSalesJpy: number
    /** 当月の保険料（カード決済額 × 料率、月額下限あり） */
    premiumJpy: number
    annualPremiumJpy: number
    /** 年間の補填上限 */
    annualCapJpy: number
    /** 1 件あたりの取消し額の補填上限 */
    perCaseCapJpy: number
    /** 年間上限の残り */
    remainingCapJpy: number
    /** 契約期間中に既に補填した額（申立てから導出。カウンタは持たない） */
    coveredToDateJpy: number
    /** 実際に補填される額（1 件あたり・年間の上限で切られた後） */
    coveredJpy: number
    /** 上限を超えて補填されないぶん。**隠さない** */
    uncoveredJpy: number
    /** 申立ての請求額の合計（補填前） */
    claimJpy: number
    /** 件ごとの判定。covered / capped / out_of_term */
    perCase: { disputeId?: string; occurredAt?: string; claimJpy: number; coveredJpy: number; reason: string }[]
    /** 未加入のときの試算か（「入っていればこうなっていた」） */
    simulated: boolean
    /** 補填 − 保険料。正なら店舗の得 */
    netJpy: number
    rate: number
    termMonths: number
    waitingDays: number
  }
  /** 引き受け対象の店舗（対面の飲食・物販のみ）。**画面で必ず出す** */
  eligibleStores: { storeId: string; storeName: string }[]
  /** 対象店舗が 1 つでもあるか。無ければ加入できない */
  eligible: boolean
}

export interface StatementPerStore {
  storeId: string
  storeName: string
  revenueJpy: number
  stripeFeeJpy: number
  platformFeeJpy: number
  netRevenueJpy: number
  cashRevenueJpy: number
  /** #1655 カード決済額。保証の保険料はこの合算から算定される */
  cashlessRevenueJpy?: number
  visitors: number
  /** #1651 申し立て手数料。stripeFeeJpy には含まれない */
  disputeFeeJpy?: number
  /** #2181 他社の決済端末の手数料の目安 (この店の手数料率から) */
  storeTerminalFeeEstimateJpy?: number
}

/** 今月の収支。全店舗の売上から手数料とプラットフォームへの支払いを引いた手取り。 */
export interface BillingStatement {
  ownerId: string
  month: string
  storeCount: number
  revenueJpy: number
  cashRevenueJpy: number
  cashlessRevenueJpy: number
  stripeFeeJpy: number
  platformFeeJpy: number
  /** #1515 返金 (売上合計は返金前の額) と、客都合キャンセルで店に残った額の内数 */
  stripeRefundJpy?: number
  /** #508 ③: 異議申立てで取り消された売上 (Stripe 側で既に引かれている) */
  disputedLostJpy?: number
  /** #1651 申し立て手数料。stripeFeeJpy には含めず独立して返す (画面で別行にする) */
  disputeFeeJpy?: number
  cashRefundJpy?: number
  /** #2162 店の端末の返金 (#2181 売上合計に足し戻してある) */
  storeTerminalRefundJpy?: number
  /** #2308 ② オンラインの返金 (PayPay・PayPal・銀行振込) */
  onlineRefundJpy?: number
  /** #2181 キャッシュレスのうち他社の決済端末の売上 */
  storeTerminalRevenueJpy?: number
  /** #2181 他社の決済端末の手数料の目安 (店ごとの手数料率から。takeHomeJpy には含めない) */
  storeTerminalFeeEstimateJpy?: number

  cancelFeeIncomeJpy?: number
  handlingFeeIncomeJpy?: number
  /** 売上 - 返金 - 決済手数料 */
  netRevenueJpy: number
  /** プラットフォームへの支払い (今は Firestore の超過分のみ) */
  platformChargesJpy: number
  /** 手取り */
  takeHomeJpy: number
  visitors: number
  perStore: StatementPerStore[]
  billing: BillingSummary
  taxIncluded: boolean
}

export const billingApi = {
  getBillingStatement: async (): Promise<BillingStatement> => {
    const res = await apiFetch('/billing/owner/me/statement')
    return res.json()
  },

  /**
   * #1655 チャージバック保証（有償オプション・**未解禁**）。
   *
   * backend も `CHARGEBACK_GUARANTEE_ENABLED` で 404 にしている。画面の gate
   * (isLicensePendingDemoEnabled) と二重に閉じる — 有償の「保証」は保険業法・
   * 保証業の該当性が未確認のため (#605 で確認する)。
   */
  getChargebackGuarantee: async (): Promise<ChargebackGuarantee> => {
    const res = await apiFetch('/billing/owner/me/chargeback-guarantee')
    return res.json()
  },

  setChargebackGuarantee: async (enrolled: boolean): Promise<void> => {
    await apiFetch('/billing/owner/me/chargeback-guarantee', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ enrolled }),
    })
  },

  getBillingSummary: async (): Promise<BillingSummary> => {
    const res = await apiFetch('/billing/owner/me/summary')
    return res.json()
  },

  getBillingCharges: async (): Promise<{ charges: BillingCharge[] }> => {
    const res = await apiFetch('/billing/owner/me/charges')
    return res.json()
  },

  /** 超過分を 1 枠買ってブロックを解除する。Stripe 残高から引き落とす。 */
  payOverage: async () => {
    const res = await apiFetch('/billing/owner/me/pay-overage', { method: 'POST' })
    return res.json()
  },

  /**
   * #1689 月額 SaaS 利用料の契約状態・提示条件・告知の段階（**未解禁**）。
   *
   * backend も `SAAS_SUBSCRIPTION_ENABLED` で 404 にしている。金額・プラン・
   * 移行方法が経営判断として決まるまで prod では開けない。
   */
  getSaasSubscription: async (): Promise<SaasSubscriptionState> => {
    const res = await apiFetch('/billing/owner/me/subscription')
    return res.json()
  },

  /** 申込み（有料プランの意思表示）。**画面が出していた条件のハッシュを添える。** */
  agreeSaasSubscription: async (offerHash: string): Promise<SaasSubscriptionState> => {
    const res = await apiFetch('/billing/owner/me/subscription', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ offerHash }),
    })
    return res.json()
  },

  /** 未払いをその場で払う。**次のバッチを待たせない** — カードを直した店が
   *  その場で解決できないと「直したのにまだ止まっている」が残る。 */
  paySaasOutstanding: async (): Promise<SaasSubscriptionState> => {
    const res = await apiFetch('/billing/owner/me/subscription/pay', { method: 'POST' })
    return res.json()
  },

  cancelSaasSubscription: async (): Promise<SaasSubscriptionState> => {
    const res = await apiFetch('/billing/owner/me/subscription', { method: 'DELETE' })
    return res.json()
  },
}
